/**
 * TanStack Query hooks for newsbot API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  Feed,
  FeedHealth,
  CreateFeedInput,
  UpdateFeedInput,
  Story,
  Article,
  Thread,
  CreateThreadInput,
  DashboardStats,
  FeedRefreshResult,
  RankingResult,
  Category,
  CategoryWithStats,
  CreateCategoryInput,
  UpdateCategoryInput,
  CategoryStats,
  CategorizationResult,
  KeywordTestResult,
  Prompt,
  PromptWithDefault,
  SuggestionsResponse,
  CachedSuggestionsResponse,
  BatchStatusResponse,
  FilterAction,
  CreateFilterActionInput,
  UpdateFilterActionInput,
  PaginatedStories,
  BlocklistRule,
  BlocklistRuleCreate,
  FeedGroup,
  FeedGroupCounts,
  TrendsResponse,
  IntegrationToken,
  CreatedIntegrationToken,
} from '../types';

// Query key factories
export const feedKeys = {
  all: ['feeds'] as const,
  detail: (id: string) => ['feeds', id] as const,
  articles: (id: string) => ['feeds', id, 'articles'] as const,
  health: () => [...feedKeys.all, 'health'] as const,
};

export const storyKeys = {
  all: ['stories'] as const,
  top: (limit: number, hours: number, feedGroup?: string | null) => ['stories', 'top', limit, hours, feedGroup] as const,
  detail: (id: string) => ['stories', id] as const,
  articles: (id: string) => ['stories', id, 'articles'] as const,
  search: (query: string) => ['stories', 'search', query] as const,
  byCategory: (category: string, tags?: string[]) => ['stories', 'category', category, tags] as const,
};

export const articleKeys = {
  unclustered: ['articles', 'unclustered'] as const,
};

export const threadKeys = {
  all: ['threads'] as const,
  active: ['threads', 'active'] as const,
  detail: (id: string) => ['threads', id] as const,
};

export const feedGroupKeys = {
  all: ['feed-groups'] as const,
  counts: ['feed-groups', 'counts'] as const,
};

export const statsKeys = {
  dashboard: ['stats', 'dashboard'] as const,
};

export const categoryKeys = {
  all: ['categories'] as const,
  withStats: ['categories', 'withStats'] as const,
  stats: ['categories', 'stats'] as const,
  detail: (id: string) => ['categories', id] as const,
  actions: (filterId: string) => ['categories', filterId, 'actions'] as const,
  action: (filterId: string, actionId: string) => ['categories', filterId, 'actions', actionId] as const,
};

// Alias for cleaner UI code (filters = categories)
export const filterKeys = categoryKeys;

export function useFeeds() {
  return useQuery({
    queryKey: feedKeys.all,
    queryFn: () => api.get<Feed[]>('/feeds'),
    staleTime: 30_000,
  });
}

export function useFeed(id: string) {
  return useQuery({
    queryKey: feedKeys.detail(id),
    queryFn: () => api.get<Feed>(`/feeds/${id}`),
    enabled: !!id,
  });
}

export function useCreateFeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFeedInput) => api.post<Feed>('/feeds', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      queryClient.invalidateQueries({ queryKey: statsKeys.dashboard });
    },
  });
}

export function useUpdateFeed(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateFeedInput) => api.put<Feed>(`/feeds/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      queryClient.invalidateQueries({ queryKey: feedKeys.detail(id) });
    },
  });
}

export function useDeleteFeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/feeds/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      queryClient.invalidateQueries({ queryKey: statsKeys.dashboard });
    },
  });
}

export function useRefreshFeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<FeedRefreshResult>(`/feeds/${id}/refresh`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: feedKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: statsKeys.dashboard });
    },
  });
}

export function useFeedHealth() {
  return useQuery({
    queryKey: feedKeys.health(),
    queryFn: () => api.get<FeedHealth[]>('/feeds/health'),
    staleTime: 30_000,
  });
}

export function useFeedGroups() {
  return useQuery({
    queryKey: feedGroupKeys.all,
    queryFn: () => api.get<FeedGroup[]>('/feed-groups'),
    staleTime: 30_000,
  });
}

export function useFeedGroupCounts() {
  return useQuery({
    queryKey: feedGroupKeys.counts,
    queryFn: () => api.get<FeedGroupCounts>('/feed-groups/counts'),
    staleTime: 30_000,
  });
}

export function useStories(params?: { limit?: number; offset?: number; sort_by?: string; sort_dir?: string; min_importance?: number; search?: string; feed_group?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  if (params?.sort_by) searchParams.set('sort_by', params.sort_by);
  if (params?.sort_dir) searchParams.set('sort_dir', params.sort_dir);
  if (params?.min_importance !== undefined) searchParams.set('min_importance', String(params.min_importance));
  if (params?.search) searchParams.set('search', params.search);
  if (params?.feed_group) searchParams.set('feed_group', params.feed_group);
  const query = searchParams.toString();

  return useQuery({
    queryKey: [...storyKeys.all, params?.limit, params?.offset, params?.sort_by, params?.sort_dir, params?.min_importance, params?.search, params?.feed_group],
    queryFn: () => api.get<PaginatedStories>(`/stories${query ? `?${query}` : ''}`),
    staleTime: 60_000,
  });
}

export function useTopStories(limit: number = 10, hours: number = 24, feedGroup?: string | null) {
  return useQuery({
    queryKey: storyKeys.top(limit, hours, feedGroup),
    queryFn: () => {
      let url = `/stories/top?limit=${limit}&hours=${hours}`;
      if (feedGroup) url += `&feed_group=${feedGroup}`;
      return api.get<Story[]>(url);
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useStory(id: string) {
  return useQuery({
    queryKey: storyKeys.detail(id),
    queryFn: () => api.get<Story>(`/stories/${id}`),
    enabled: !!id,
  });
}

export function useStoryArticles(id: string) {
  return useQuery({
    queryKey: storyKeys.articles(id),
    queryFn: () => api.get<Article[]>(`/stories/${id}/articles`),
    enabled: !!id,
    retry: false,
    staleTime: 60_000,
  });
}

export function useSearchStories(query: string) {
  return useQuery({
    queryKey: storyKeys.search(query),
    queryFn: () => api.get<Story[]>(`/stories/search?q=${encodeURIComponent(query)}`),
    enabled: query.length >= 2,
    staleTime: 30_000,
  });
}

export function useUnclusteredArticles() {
  return useQuery({
    queryKey: articleKeys.unclustered,
    queryFn: () => api.get<Article[]>('/articles/unclustered'),
    staleTime: 30_000,
  });
}

export function useThreads() {
  return useQuery({
    queryKey: threadKeys.all,
    queryFn: () => api.get<Thread[]>('/threads'),
    staleTime: 60_000,
  });
}

export function useActiveThreads() {
  return useQuery({
    queryKey: threadKeys.active,
    queryFn: () => api.get<Thread[]>('/threads?active=true'),
    staleTime: 60_000,
  });
}

export function useThread(id: string) {
  return useQuery({
    queryKey: threadKeys.detail(id),
    queryFn: () => api.get<Thread>(`/threads/${id}`),
    enabled: !!id,
  });
}

export function useCreateThread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateThreadInput) => api.post<Thread>('/threads', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: threadKeys.all });
      queryClient.invalidateQueries({ queryKey: statsKeys.dashboard });
    },
  });
}

export function useAddStoryToThread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, storyId }: { threadId: string; storyId: string }) =>
      api.post<void>(`/threads/${threadId}/stories`, { story_id: storyId }),
    onSuccess: (_, { threadId }) => {
      queryClient.invalidateQueries({ queryKey: threadKeys.detail(threadId) });
    },
  });
}

export function useDashboardStats() {
  return useQuery({
    queryKey: statsKeys.dashboard,
    queryFn: () => api.get<DashboardStats>('/stats'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useRunHeuristicRanking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<RankingResult>('/ranking/heuristic'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: storyKeys.all });
    },
  });
}

export function useRunLLMRanking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params?: { batch_size?: number }) =>
      api.post<RankingResult>('/ranking/llm', params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: storyKeys.all });
    },
  });
}

export function useCategories(params?: { feed_group_id?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.feed_group_id) searchParams.set('feed_group_id', params.feed_group_id);
  const query = searchParams.toString();

  return useQuery({
    queryKey: [...categoryKeys.all, params?.feed_group_id],
    queryFn: () => api.get<Category[]>(`/categories${query ? `?${query}` : ''}`),
    staleTime: 60_000,
  });
}

export function useCategoriesWithStats(params?: { feed_group_id?: string }) {
  const searchParams = new URLSearchParams();
  searchParams.set('include_stats', 'true');
  if (params?.feed_group_id) searchParams.set('feed_group_id', params.feed_group_id);
  const query = searchParams.toString();

  return useQuery({
    queryKey: [...categoryKeys.withStats, params?.feed_group_id],
    queryFn: () => api.get<CategoryWithStats[]>(`/categories?${query}`),
    staleTime: 60_000,
  });
}

export function useCategory(id: string) {
  return useQuery({
    queryKey: categoryKeys.detail(id),
    queryFn: () => api.get<Category>(`/categories/${id}`),
    enabled: !!id,
  });
}

export function useCategoryStats() {
  return useQuery({
    queryKey: categoryKeys.stats,
    queryFn: () => api.get<CategoryStats>('/categories/stats'),
    staleTime: 60_000,
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCategoryInput) => api.post<Category>('/categories', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: categoryKeys.withStats });
      queryClient.invalidateQueries({ queryKey: categoryKeys.stats });
    },
  });
}

export function useUpdateCategory(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateCategoryInput) => api.put<Category>(`/categories/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: categoryKeys.withStats });
      queryClient.invalidateQueries({ queryKey: categoryKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: categoryKeys.stats });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/categories/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: categoryKeys.withStats });
      queryClient.invalidateQueries({ queryKey: categoryKeys.stats });
      queryClient.invalidateQueries({ queryKey: storyKeys.all });
    },
  });
}

export function useToggleCategoryEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch<Category>(`/categories/${id}/enabled`, { enabled }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: categoryKeys.withStats });
      queryClient.invalidateQueries({ queryKey: categoryKeys.detail(id) });
    },
  });
}

export function useTestKeywords() {
  return useMutation({
    mutationFn: (data: { text: string; keywords: string[] }) =>
      api.post<KeywordTestResult>('/categories/test-keywords', data),
  });
}

export interface StoryCategoryParams {
  category?: string;
  tags?: string[];
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_dir?: string;
  min_importance?: number;
  search?: string;
  feed_group?: string;
}

export function useStoriesByCategory(params: StoryCategoryParams) {
  const searchParams = new URLSearchParams();
  if (params.category) searchParams.set('category', params.category);
  if (params.tags?.length) searchParams.set('tags', params.tags.join(','));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.offset) searchParams.set('offset', String(params.offset));
  if (params.sort_by) searchParams.set('sort_by', params.sort_by);
  if (params.sort_dir) searchParams.set('sort_dir', params.sort_dir);
  if (params.min_importance !== undefined) searchParams.set('min_importance', String(params.min_importance));
  if (params.search) searchParams.set('search', params.search);
  if (params.feed_group) searchParams.set('feed_group', params.feed_group);
  const query = searchParams.toString();

  return useQuery({
    queryKey: [...storyKeys.all, 'filtered', params.category, params.tags, params.limit, params.offset, params.sort_by, params.sort_dir, params.min_importance, params.search, params.feed_group],
    queryFn: () => api.get<PaginatedStories>(`/stories${query ? `?${query}` : ''}`),
    staleTime: 60_000,
    enabled: !!params.category,
  });
}

export function useRunCategorization() {
  const queryClient = useQueryClient();
  return useMutation({
    // force=true re-checks all uncategorized stories against categories
    // This ensures newly added categories are applied to existing stories
    mutationFn: () => api.post<CategorizationResult>('/stories/categorize?force=true&limit=500'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: storyKeys.all });
      queryClient.invalidateQueries({ queryKey: categoryKeys.stats });
      queryClient.invalidateQueries({ queryKey: categoryKeys.withStats });
    },
  });
}

export function useCategorizeStory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (storyId: string) => api.post<Story>(`/stories/${storyId}/categorize`),
    onSuccess: (_, storyId) => {
      queryClient.invalidateQueries({ queryKey: storyKeys.detail(storyId) });
      queryClient.invalidateQueries({ queryKey: storyKeys.all });
      queryClient.invalidateQueries({ queryKey: categoryKeys.stats });
    },
  });
}

interface AICategorizeResult {
  status: string;
  categorized: number;
  processed: number;
  message: string;
}

export function useRunAICategorization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (limit: number = 20) => api.post<AICategorizeResult>(`/stories/ai-categorize?limit=${limit}`),
    onSuccess: () => {
      // Invalidate stats and stories after categorization completes
      queryClient.invalidateQueries({ queryKey: categoryKeys.stats });
      queryClient.invalidateQueries({ queryKey: storyKeys.all });
    },
  });
}

export const promptKeys = {
  all: ['prompts'] as const,
  detail: (name: string) => ['prompts', name] as const,
};

export const suggestionKeys = {
  all: ['suggestions'] as const,
};

export const batchKeys = {
  status: (detailed: boolean) => ['batches', 'status', detailed] as const,
};

export function useCachedSuggestions() {
  return useQuery({
    queryKey: suggestionKeys.all,
    queryFn: () => api.get<CachedSuggestionsResponse>('/suggestions'),
    staleTime: 60_000, // Consider stale after 1 minute
  });
}

export function usePrompts() {
  return useQuery({
    queryKey: promptKeys.all,
    queryFn: () => api.get<PromptWithDefault[]>('/prompts'),
    staleTime: 60_000,
  });
}

export function usePrompt(name: string) {
  return useQuery({
    queryKey: promptKeys.detail(name),
    queryFn: () => api.get<PromptWithDefault>(`/prompts/${name}`),
    enabled: !!name,
  });
}

export function useUpdatePrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, content }: { name: string; content: string }) =>
      api.put<Prompt>(`/prompts/${name}`, { content }),
    onSuccess: (_, { name }) => {
      queryClient.invalidateQueries({ queryKey: promptKeys.all });
      queryClient.invalidateQueries({ queryKey: promptKeys.detail(name) });
    },
  });
}

export function useResetPrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.delete<void>(`/prompts/${name}`),
    onSuccess: (_, name) => {
      queryClient.invalidateQueries({ queryKey: promptKeys.all });
      queryClient.invalidateQueries({ queryKey: promptKeys.detail(name) });
    },
  });
}

export function useGenerateSuggestions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params?: { story_limit?: number; force_refresh?: boolean }) =>
      api.post<SuggestionsResponse>('/suggestions', params || {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: suggestionKeys.all });
    },
  });
}

export function useApplyKeywordSuggestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { category_id: string; keywords: string[] }) =>
      api.post<void>('/suggestions/apply-keywords', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: categoryKeys.withStats });
      queryClient.invalidateQueries({ queryKey: suggestionKeys.all });
    },
  });
}

export function useApplyNewCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description: string; keywords: string[] }) =>
      api.post<Category>('/suggestions/apply-new-category', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: categoryKeys.withStats });
      queryClient.invalidateQueries({ queryKey: suggestionKeys.all });
    },
  });
}

export const useBatchStatus = (detailed: boolean = true) => {
  return useQuery({
    queryKey: batchKeys.status(detailed),
    queryFn: () => api.get<BatchStatusResponse>(`/batches/status?detailed=${detailed}`),
    refetchInterval: 30000, // Refresh every 30 seconds
  });
};

export function useFilterActions(filterId: string) {
  return useQuery({
    queryKey: categoryKeys.actions(filterId),
    queryFn: () => api.get<FilterAction[]>(`/filters/${filterId}/actions`),
    enabled: !!filterId,
    staleTime: 60_000,
  });
}

export function useFilterAction(filterId: string, actionId: string) {
  return useQuery({
    queryKey: categoryKeys.action(filterId, actionId),
    queryFn: () => api.get<FilterAction>(`/filters/${filterId}/actions/${actionId}`),
    enabled: !!filterId && !!actionId,
  });
}

export function useCreateFilterAction(filterId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFilterActionInput) =>
      api.post<FilterAction>(`/filters/${filterId}/actions`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.actions(filterId) });
    },
  });
}

export function useUpdateFilterAction(filterId: string, actionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateFilterActionInput) =>
      api.put<FilterAction>(`/filters/${filterId}/actions/${actionId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.actions(filterId) });
      queryClient.invalidateQueries({ queryKey: categoryKeys.action(filterId, actionId) });
    },
  });
}

export function useDeleteFilterAction(filterId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (actionId: string) =>
      api.delete<void>(`/filters/${filterId}/actions/${actionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.actions(filterId) });
    },
  });
}

export const trendKeys = {
  all: ['trends'] as const,
  view: (vertical: string, window: string) =>
    [...trendKeys.all, vertical, window] as const,
};

export function useTrends(vertical: string, window: string) {
  return useQuery({
    queryKey: trendKeys.view(vertical, window),
    queryFn: () =>
      api.get<TrendsResponse>(
        `/trends/entities?vertical=${vertical}&window=${window}`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export const integrationTokenKeys = {
  all: ['integration-tokens'] as const,
};

export function useIntegrationTokens() {
  return useQuery({
    queryKey: integrationTokenKeys.all,
    queryFn: () => api.get<IntegrationToken[]>('/integration-tokens'),
  });
}

export function useCreateIntegrationToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api.post<CreatedIntegrationToken>('/integration-tokens', { name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: integrationTokenKeys.all }),
  });
}

export function useRevokeIntegrationToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/integration-tokens/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: integrationTokenKeys.all }),
  });
}

export const blocklistKeys = {
  all: ['blocklist'] as const,
  list: () => [...blocklistKeys.all, 'list'] as const,
};

export function useBlocklistRules() {
  return useQuery({
    queryKey: blocklistKeys.list(),
    queryFn: () => api.get<BlocklistRule[]>('/blocklist'),
    staleTime: 60_000,
  });
}

export function useCreateBlocklistRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rule: BlocklistRuleCreate) => api.post<BlocklistRule>('/blocklist', rule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: blocklistKeys.all });
    },
  });
}

export function useUpdateBlocklistRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch<BlocklistRule>(`/blocklist/${id}`, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: blocklistKeys.all });
    },
  });
}

export function useDeleteBlocklistRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/blocklist/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: blocklistKeys.all });
    },
  });
}
